import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookOpen, Download, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { PageShell } from "../components/PageShell";
import { ShareCardButton } from "../components/ShareCardButton";
import { useFutureSelf } from "../lib/futureSelf";
import { formatApiError } from "../context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

const TYPES = { reflection: "Reflections", conversation: "Conversations", comparison: "Comparisons", five_decisions: "Five Decisions",
  council: "Council", letter: "Letters", goal: "Goals", progress: "Progress", regret: "Regret reversals", note: "Notes" };

const Body = ({ e, fs }) => {
  const c = e.content;
  if (e.type === "reflection") return (<>
    <div className="text-sm text-cyan-100/80 border-l-2 border-cyan-400/40 pl-4 mb-3">{c.answer}</div>
    <div className="text-sm italic text-amber-100/90 border-l-2 border-amber-300/50 pl-4">{c.reply}</div>
    <ShareCardButton fs={fs} message={c.reply} years={c.years} testId={`journal-share-${e.id}`} className="mt-4" /></>);
  if (e.type === "conversation") return <div className="space-y-1 text-sm max-h-48 overflow-y-auto">{(c.messages || []).map((m, i) => <p key={i} className={m.role === "user" ? "text-cyan-100/80" : "italic text-amber-100/85"}>{m.role === "user" ? "You: " : ""}{m.content}</p>)}</div>;
  if (e.type === "five_decisions") return <ul className="text-sm space-y-1">{c.map((d, i) => <li key={i}><span className="font-mono-label mr-2">{d.category} · {d.kind}</span>{d.text}</li>)}</ul>;
  if (e.type === "council") return <div className="text-sm space-y-1 max-h-48 overflow-y-auto">{c.flatMap((r) => r.responses).map((r, i) => <p key={i}><span className="text-amber-200 capitalize">{r.persona}: </span>{r.text}</p>)}</div>;
  if (e.type === "comparison") return <div className="text-sm space-y-1 max-h-48 overflow-y-auto">{(c.turns || []).map((t, i) => <p key={i}><span className="text-purple-200">{t.speaker}: </span>{t.text}</p>)}{c.answers && <><p>A: {c.answers.a}</p><p>B: {c.answers.b}</p></>}</div>;
  if (e.type === "regret") return <div className="text-sm"><p className="italic mb-2">{c.scenario}</p><ol className="list-decimal pl-5">{(c.steps || []).map((s) => <li key={s}>{s}</li>)}</ol></div>;
  if (e.type === "progress") return <div className="text-sm"><p className="text-cyan-100/80">{c.text}</p><p className="italic text-amber-100/85">{c.reply}</p></div>;
  return <p className="text-sm whitespace-pre-line text-white/80 max-h-60 overflow-y-auto">{typeof c === "string" ? c : JSON.stringify(c)}</p>;
};

export default function Journal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fs = useFutureSelf(id);
  const [entries, setEntries] = useState(null);
  const [filter, setFilter] = useState("all");
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(() => api.get(`/journey/${id}/journal`).then(({ data }) => setEntries(data)), [id]);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => (entries || []).filter((e) => filter === "all" || e.type === filter), [entries, filter]);
  const groups = useMemo(() => shown.reduce((acc, e) => {
    const d = new Date(e.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
    (acc[d] = acc[d] || []).push(e);
    return acc;
  }, {}), [shown]);

  const remove = async (e) => { await api.delete(`/journal/${e.id}`, { params: { journey_id: id } }); load(); };
  const exportAll = async () => {
    const { data } = await api.get(`/journey/${id}/export`);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    a.download = "rabbit-hole-futures-export.json";
    a.click();
  };
  const deleteAll = async () => {
    try { await api.delete(`/journey/${id}`); localStorage.removeItem("life_rabbit_hole_state_v1"); toast.success("Your journey and its data were deleted."); navigate("/"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resume = (e) => (e.type === "conversation" ? navigate(`/self/${e.content.self_id}`) : e.type === "letter" ? navigate(`/letters/${id}`) : null);

  return (
    <PageShell back={`/journey/${id}`} label="Reflection journal" title={<>Your <span className="text-glow-gold">reflection</span> journal</>}
      subtitle="Every question, conversation, comparison, letter and goal from across your timelines, organised by date.">
      <div className="flex flex-wrap gap-2 mb-6" data-testid="journal-filters">
        {["all", ...Object.keys(TYPES)].map((t) => <button key={t} onClick={() => setFilter(t)} data-testid={`journal-filter-${t}`}
          className={`text-[11px] px-3 py-1.5 rounded-full border ${filter === t ? "border-amber-300 text-amber-200" : "border-white/10 text-white/55"}`}>{t === "all" ? "All" : TYPES[t]}</button>)}
        <button onClick={exportAll} className="btn-pill text-xs ml-auto" data-testid="journal-export"><Download size={12} /> Export</button>
        <button onClick={() => setConfirm(true)} className="btn-pill text-xs text-red-300" data-testid="journal-delete-all"><Trash2 size={12} /> Delete my data</button>
      </div>
      {entries && !shown.length && <div className="glass rounded-3xl p-10 text-center text-white/60" data-testid="journal-empty"><BookOpen className="mx-auto mb-3" />Nothing here yet.</div>}
      <div data-testid="journal-list">
        {Object.entries(groups).map(([day, list]) => (
          <div key={day} className="mb-8"><div className="font-mono-label mb-3">{day}</div>
            <div className="space-y-4">{list.map((e, i) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.4) }} className="glass rounded-2xl p-5" data-testid={`journal-entry-${e.id}`}>
                <div className="flex justify-between gap-3 mb-2">
                  <div className="font-mono-label">{TYPES[e.type] || e.type}{e.timeline ? ` · ${e.timeline}` : ""}</div>
                  <div className="flex gap-2">
                    {["conversation", "letter"].includes(e.type) && <button onClick={() => resume(e)} className="text-[11px] text-cyan-300 hover:text-cyan-100" data-testid={`journal-resume-${e.id}`}>Resume</button>}
                    <button onClick={() => remove(e)} className="text-white/35 hover:text-red-300" aria-label="Delete entry" data-testid={`journal-delete-${e.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
                <div className="font-display text-lg mb-3">{e.title}</div>
                <Body e={e} fs={fs} />
              </motion.div>))}</div>
          </div>))}
      </div>
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="glass-strong border-white/10 text-white">
          <DialogHeader><DialogTitle>Delete everything?</DialogTitle>
            <DialogDescription className="text-white/60">This permanently removes this journey, its alternate selves, conversations, letters, goals, journal and photos.</DialogDescription></DialogHeader>
          <div className="flex gap-3 justify-end"><button onClick={() => setConfirm(false)} className="btn-pill text-xs">Cancel</button>
            <button onClick={deleteAll} className="btn-pill text-xs text-red-300 border-red-400/50" data-testid="journal-delete-confirm">Delete permanently</button></div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
